/**
 * Chat Store for AccountsGenie
 * Following PRD Phase 3 specifications (lines 340-407)
 * Manages chat messages, sessions, and real-time communication
 */

import { create } from 'zustand';
import { ChatState } from './types';
import { createChatActions } from './actions';
import { createWebSocketHandlers } from './websocket';

// Helper to compute todo stats
const computeTodoStats = (todos: Array<{ status: string }>) => {
  const total = todos.length;
  const pending = todos.filter(t => t.status === 'pending').length;
  const inProgress = todos.filter(t => t.status === 'in_progress').length;
  const completed = todos.filter(t => t.status === 'completed').length;
  const percentComplete = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { total, pending, inProgress, completed, percentComplete };
};

export const useChatStore = create<ChatState>((set, get) => {
  // Create actions
  const actions = createChatActions(set, get);
  const wsHandlers = createWebSocketHandlers(set, get);

  return {
    // Initial state
    currentSession: null,
    sessionId: null,
    currentSessionId: undefined, // Alias for compatibility
    messages: [],
    isLoading: false,
    isConnected: false,
    wsConnected: false, // Alias for WebSocket connection status
    isApiAvailable: true, // Assume API is available until proven otherwise
    isTyping: false,
    ws: null,
    currentPlan: null,
    executionUpdates: [],
    isExecuting: false,
    executionStartTime: undefined,
    hasExecutionCompleted: false,

    // Interactive execution state (like Claude Code ESC key support)
    executionState: 'idle' as const,
    canInterrupt: false,

    // Agent Todo Tracking (from Claude SDK TodoWrite tool)
    agentTodos: [],
    todoStats: { total: 0, pending: 0, inProgress: 0, completed: 0, percentComplete: 0 },
    isTodoPanelOpen: false,

    // Step progress tracking
    stepProgress: {
      activeSteps: [],
      lastCompletedStep: 0,
      isRunning: false,
      startTime: undefined,
      totalSteps: 0,
      totalDuration: 0,
      executionCount: 0
    },

    // Agent Todo Tracking actions
    setAgentTodos: (todos) => {
      const stats = computeTodoStats(todos);
      set({ agentTodos: todos, todoStats: stats });
      // Auto-open panel when todos arrive
      if (todos.length > 0 && !get().isTodoPanelOpen) {
        set({ isTodoPanelOpen: true });
      }
    },
    clearAgentTodos: () => set({
      agentTodos: [],
      todoStats: { total: 0, pending: 0, inProgress: 0, completed: 0, percentComplete: 0 }
    }),
    toggleTodoPanel: () => set(state => ({ isTodoPanelOpen: !state.isTodoPanelOpen })),
    setTodoPanelOpen: (isOpen) => set({ isTodoPanelOpen: isOpen }),

    // Step progress actions
    updateStepProgress: (stepNum: number, status: 'started' | 'done' | 'failed') => {
      set(state => {
        const current = state.stepProgress;

        if (status === 'started') {
          // Show only the current step (replace, don't accumulate)
          return {
            stepProgress: {
              ...current,
              activeSteps: [stepNum],
              isRunning: true,
              startTime: current.startTime || Date.now(),
              completionSummary: undefined
            }
          };
        } else {
          // Step done or failed - update lastCompleted, clear active
          return {
            stepProgress: {
              ...current,
              activeSteps: [],
              lastCompletedStep: Math.max(current.lastCompletedStep, stepNum),
              isRunning: false
            }
          };
        }
      });
    },

    clearStepProgress: () => set(state => ({
      stepProgress: {
        ...state.stepProgress,
        activeSteps: [],
        lastCompletedStep: 0,
        isRunning: false,
        startTime: undefined
        // Keep totals - they persist across the session
      }
    })),

    completeExecution: () => {
      const state = get();
      const current = state.stepProgress;
      const duration = current.startTime ? Date.now() - current.startTime : 0;
      const steps = current.lastCompletedStep;

      // Update totals
      set({
        stepProgress: {
          ...current,
          activeSteps: [],
          isRunning: false,
          totalSteps: current.totalSteps + steps,
          totalDuration: current.totalDuration + duration,
          executionCount: current.executionCount + 1
        }
      });

      return { steps, duration };
    },

    // Merge actions
    ...actions,
    ...wsHandlers
  };
});